import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { graphql } from 'gql';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { methodologyMapReverse } from '../types/methodology';

import { useProjectData } from './useProjectData';
import { useProjectParams } from './useProjectParams';
import { useSendGpnAnalyticsEvent } from './useSendGpnAnalyticsEvent';

const projectDeleteGql = graphql(`
    mutation deleteProject($uniqueId: String!) {
        deleteProject(uniqueId: $uniqueId) {
            data
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

export const useMethodsProjectDelete = (projectId?: string) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { methodology } = useProjectParams();
    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

    const [deleteData, deleteStatus, deleteError, deleteProject] = useRequest({
        requestId: 'deleteProject',
        query: projectDeleteGql,
        withEffect: false,
    });

    const [makeResetDeleteData] = useResetStoreData({ requestId: 'deleteProject' });
    const closeErrorModalHandler = useCallback(() => {
        makeResetDeleteData(null);
        setIsErrorDialogOpen(false);
    }, [makeResetDeleteData]);

    const handleDeleteExactly = useCallback(() => {
        if (projectId) {
            dispatch(deleteProject({ uniqueId: projectId }));
        }
    }, [deleteProject, dispatch, projectId]);

    const handleDeleteBtn = useCallback(() => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_create project delete button_pressed',
            projectId,
            methodology: methodology ? methodologyMapReverse[methodology] : undefined,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });

        setIsConfirmationDialogOpen(true);
    }, [
        methodology,
        projectData?.contractType,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const handleCloseDeleteDialog = useCallback(() => {
        setIsConfirmationDialogOpen(false);
    }, []);

    const handleCreateReportDownload = () => {
        ExportCSVReport(deleteData?.deleteProject?.validation);
    };

    useEffect(() => {
        if (deleteData?.deleteProject?.data === projectId) {
            navigate('/projects');
        } else if (deleteData?.deleteProject?.message) {
            setIsErrorDialogOpen(true);
        }
    }, [deleteData, projectId, navigate]);

    useEffect(
        () => () => {
            setIsConfirmationDialogOpen(false);
        },
        [],
    );

    return {
        isConfirmationDialogOpen,
        isErrorDialogOpen,
        closeErrorModalHandler,
        deleteData,
        deleteStatus,
        deleteError,
        deleteProject,
        handleCloseDeleteDialog,
        handleDeleteExactly,
        handleDeleteBtn,
        handleCreateReportDownload,
    };
};
